import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { closeEditUnit } from 'reducers/DialogsReducer';
import Dialog from 'components/Dialog';
import formStyles, { gridStyle, Row, RowInner, ColCard } from 'utils/formStyles';
import Autocomplete2 from 'components/Autocomplete2';
import TextField2 from 'components/TextField2';
import PersonLookup from 'components/PersonLookup';
import PlaceLookup from 'components/PlaceLookup';
import { editUnit } from 'reducers/SearchReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import { notify } from 'reducers/NotifierReducer';
import { handleError } from 'reducers/ErrorReducer';
import Dictionary from 'components/Dictionary';

const useStyles = makeStyles((theme) => ({
  ...formStyles,
  item: gridStyle(200, 800),
  field: gridStyle(210, 400),
}));

function AddUnitDialog(props) {
  const classes = useStyles();
  const { dictionary, data } = props;
  const { Agencies, Division, Zones } = dictionary;
  const ContactTypes = [{ Code: 'Person' }, { Code: 'Place' }];

  const [unit, setUnit] = useState('');
  const [agency, setAgency] = useState(null);
  const [division, setDivision] = useState(null);
  const [zone, setZone] = useState(null);
  const [contactType, setContactType] = useState(ContactTypes[0]);
  const [person, setPerson] = useState(null);
  const [place, setPlace] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    processInitialData();
  }, []);

  const processInitialData = async () => {
    const { Unit, AgencyID, Division: curDivision, Zone, ContactType, ptsContactID, Notes } = data;
    setUnit(Unit);
    const agency = Agencies.find((a) => a.AgencyID === AgencyID);
    setAgency(agency || null);
    const division = Division.find((d) => d.Code === curDivision);
    setDivision(division || null);
    const zone = Zones.find((z) => z.ZoneCode === Zone);
    setZone(zone || null);
    const contactType = ContactTypes.find((c) => c.Code === ContactType);
    setContactType(contactType || null);
    setNote(Notes || '');
  };

  const close = () => {
    props.closeEditUnit();
  };

  const save = async () => {
    const dataObj = {
      Unit: unit,
      AgencyID: agency ? agency.AgencyID : null,
      Division: division ? division.Code : null,
      Zone: zone ? zone.ZoneCode : null,
      ContactType: contactType ? contactType.Code : null,
      ptsContactID: getContactId(),
      Notes: note,
    };
    try {
      const result = await editUnit(data.ptsUnitID, dataObj);
      const { ptsUnitID } = result;
      props.notifyDataUpdate({ type: 'edit-unit', data: { ptsUnitID } });
      props.closeEditUnit();
      props.notify('Unit updated!', 'success');
    } catch (err) {
      props.handleError(err);
    }
  };

  const getContactId = () => {
    if (!contactType) return null;
    if (contactType.Code == 'Person') return person ? person.ptsPersonID : null;
    return place ? place.ptsPlaceID : null;
  };

  const renderActions = () => {
    return (
      <>
        <Button color="primary" variant="contained" autoFocus onClick={save} disabled={!unit}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const renderUnit = () => {
    const onChange = (ev, val) => setUnit(val);
    return (
      <TextField2 className={classes.item} label="Unit" value={unit} onChange={onChange} max={25} />
    );
  };

  const renderAgency = () => {
    const onChange = (ev, val) => {
      setAgency(val);
      setZone(null);
    };
    return (
      <Dictionary
        options="Agencies"
        className={classes.item}
        onChange={onChange}
        value={agency}
        label="Agency"
      />
    );
  };

  const renderDivision = () => {
    const onChange = (ev, val) => setDivision(val);
    return (
      <Dictionary
        options="Division"
        className={classes.item}
        onChange={onChange}
        value={division}
        label="Division"
      />
    );
  };

  const renderZone = () => {
    const onChange = (ev, val) => setZone(val);
    const filteredZones = Zones.filter((z) => z.AgencyID === agency?.AgencyID);
    return (
      <Autocomplete2
        options={agency ? filteredZones : Zones}
        className={classes.item}
        onChange={onChange}
        value={zone}
        label="Zone"
        getOptionSelected={(option, value) => option.ZoneCode === value.ZoneCode}
        getOptionLabel={(option) => (option.ZoneCode ? option.ZoneCode : '')}
        renderOption={(option) => (
          <span>
            {option.ZoneCode} - {option.Description} - {option.AgencyID}
          </span>
        )}
      />
    );
  };

  const renderPrimaryContactType = () => {
    const onChange = (ev, val) => setContactType(val);
    return (
      <Autocomplete2
        options={ContactTypes}
        className={classes.item}
        onChange={onChange}
        value={contactType}
        label="Primary Contact Type"
      />
    );
  };

  const renderPerson = () => {
    const handlePersonChange = (value) => setPerson(value);
    const personId =
      data.ContactType === 'Person' && typeof data.ptsContactID === 'number'
        ? data.ptsContactID
        : null;
    return (
      <PersonLookup
        className={classes.item}
        onPersonChange={handlePersonChange}
        label="Person"
        ptsPersonID={personId}
      />
    );
  };

  const renderPlace = () => {
    const handleChange = (value) => setPlace(value);
    const placeId =
      data.ContactType === 'Place' && typeof data.ptsContactID === 'number'
        ? data.ptsContactID
        : null;
    return (
      <PlaceLookup
        onChange={handleChange}
        className={classes.item}
        label="Place"
        ptsPlaceID={placeId}
      />
    );
  };

  const renderNotes = () => {
    const onChange = (ev, val) => setNote(val);
    return (
      <TextField2
        className={classes.item}
        label="Note"
        value={note}
        onChange={onChange}
        max={8000}
        rows={5}
        multiline
      />
    );
  };

  return (
    <Dialog toolbar onClose={close} title="Edit Unit" actions={renderActions()} maxWidth="sm">
      <Row>
        <ColCard minWidth={500}>
          <RowInner>
            {renderUnit()}
            {renderAgency()}
          </RowInner>
          <RowInner>
            {renderDivision()}
            {renderZone()}
          </RowInner>
          <RowInner>
            {renderPrimaryContactType()}
            {contactType?.Code == 'Person' && renderPerson()}
            {contactType?.Code == 'Place' && renderPlace()}
          </RowInner>
          <RowInner>{renderNotes()}</RowInner>
        </ColCard>
      </Row>
    </Dialog>
  );
}

const mapStateToProps = (state) => {
  return {
    dictionary: state.dictionary,
  };
};

export default connect(mapStateToProps, { closeEditUnit, notifyDataUpdate, notify, handleError })(
  AddUnitDialog
);
